// The `evidence/library` source-list api module — carved from
// `src/components/library/source-lists/api.ts` (now a delegate) at D1 slice 1,
// ARCFE-1069.
//
// WHY THESE SIX FNS COULD MOVE AND THE SEVENTH COULD NOT. The P0 census
// deferred this whole file on two blockers, and exactly one of them was real.
// `@/utils/scribeHTTP` is the agents 4.1 blocker with a shipped heal
// (`@heidi/api/client`, E5/ARCFE-805) — the census bucketed it as an
// "unbucketed app import … resolve per-case at move time", which is a
// per-file decision, not a wall. The second blocker IS a wall:
// `importSourceList` returns `CollectionDetailResponse` from
// `@/components/library/types`, and that type is the head of a ~100-line
// collections chain belonging to the `/evidence/collections` route family the
// census pins as unclaimed. So `importSourceList` stayed at the old path — a
// recorded exclusion by ROUTE, the same test that kept CPD (`/cpd/*`)
// out of this lane, not a deferral waiting on a seam.
//
// The registry's transport is the app's REAL `scribeHttpV2` singleton — same
// interceptors, same auth/refresh state — so request behaviour is byte-identical
// to the pre-move module: same paths, same verbs, same bodies, same `.data`
// unwrapping. `ScribeHttp` keeps the signatures structural: no axios type
// reaches this package's surface, and `axios` stays out of its manifest.
//
// The file is named for its RESOURCE rather than keeping the source basename
// `api.ts`, which would have landed as `data/api/api.ts`. That is a deliberate,
// disclosed deviation from the keep-the-original-basename rule, taken for the
// same reason `packages/dictate/src/data/api/preferences.ts` took it at
// ARCFE-1067 and matching every sibling package (`commerce/data/api/products.ts`,
// `agents/data/api/agent-memory.ts`). The rule bites on `jest.mock` targets;
// nothing mocks this module by path — its only importers are its own hooks and
// tests, measured before the move.
import { getScribeHttpClients } from '@heidi/api/client';

import type {
  CreateSourceListRequest,
  DeleteSourceListResponse,
  SourceList,
  UpdateSourceListRequest,
} from '../../contracts';

interface ScribeHttp {
  get<T>(url: string): Promise<{ data: T }>;
  post<T>(url: string, body?: unknown): Promise<{ data: T }>;
  put<T>(url: string, body?: unknown): Promise<{ data: T }>;
  delete<T>(url: string): Promise<{ data: T }>;
}

// `scribeHttpV2` base is /api/v2/ml-scribe/; scribe JWT injected by interceptor.
const http = (): ScribeHttp => getScribeHttpClients().scribeHttpV2;

const BASE_PATH = '/source-lists';

/**
 * Validates that a source list ID contains only safe characters.
 * Prevents SSRF by ensuring the ID cannot contain path traversal or URL manipulation.
 * Valid IDs are alphanumeric with hyphens and underscores (MongoDB ObjectId or UUID format).
 *
 * EXPORTED, where the pre-move module kept it private. `importSourceList`
 * stayed app-side (see the header) and validates its id with this exact
 * function — publishing it is what stops the residue growing a second copy of
 * a security check, which is the one thing worse than the export.
 */
export function validateSourceListId(sourceListId: string): string {
  if (!sourceListId || typeof sourceListId !== 'string') {
    throw new Error('Invalid source list ID: ID is required');
  }
  // Allow only alphanumeric characters, hyphens, and underscores
  const safeIdPattern = /^[a-zA-Z0-9_-]+$/;
  if (!safeIdPattern.test(sourceListId)) {
    throw new Error('Invalid source list ID: contains invalid characters');
  }
  return sourceListId;
}

/**
 * Create a new source list
 */
export async function createSourceList(
  data: CreateSourceListRequest
): Promise<SourceList> {
  const response = await http().post<SourceList>(`${BASE_PATH}/`, data);
  return response.data;
}

/**
 * List all source lists for the current user
 */
export async function listSourceLists(): Promise<SourceList[]> {
  const response = await http().get<SourceList[]>(`${BASE_PATH}/`);
  return response.data;
}

/**
 * Get a source list by ID
 */
export async function getSourceList(sourceListId: string): Promise<SourceList> {
  const validatedId = validateSourceListId(sourceListId);
  const response = await http().get<SourceList>(`${BASE_PATH}/${validatedId}`);
  return response.data;
}

/**
 * Update a source list
 */
export async function updateSourceList(
  sourceListId: string,
  data: UpdateSourceListRequest
): Promise<SourceList> {
  const validatedId = validateSourceListId(sourceListId);
  const response = await http().put<SourceList>(`${BASE_PATH}/${validatedId}`, data);
  return response.data;
}

/**
 * Delete a source list (soft delete)
 */
export async function deleteSourceList(
  sourceListId: string
): Promise<DeleteSourceListResponse> {
  const validatedId = validateSourceListId(sourceListId);
  const response = await http().delete<DeleteSourceListResponse>(
    `${BASE_PATH}/${validatedId}`
  );
  return response.data;
}

/**
 * List only unmigrated source lists (not yet converted to collections).
 * Used in unified mode to populate the legacy section.
 */
export async function listUnmigratedSourceLists(): Promise<SourceList[]> {
  const response = await http().get<SourceList[]>(`${BASE_PATH}/?unmigrated_only=true`);
  return response.data;
}
